/**
 * EditorOverlay - Editor UI using the Layout System
 *
 * Uses CSS Grid layout for proper panel management.
 * Features resizable, collapsible panels with keyboard shortcuts.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  EditorTool,
  LayerType,
  SurfaceType,
  TileType
} from '../../../shared/TileEditorTypes';
import { gameLogger } from '../../../utils/GameLogger';
import { ServiceRegistry } from '../../core/ServiceRegistry';
import { EditorState, EditorSystem } from '../../systems/editor/EditorSystem';
import { AppContainer, Panel, useLayout } from '../Layout';
import { TilePalette } from './components/TilePalette';
import { LayerPanel } from './components/LayerPanel';
import { EffectLayerPanel } from './components/EffectLayerPanel';
import { Toolbar } from './components/Toolbar';
import { PropertiesPanel } from './components/PropertiesPanel';
import { LevelEntity } from '../../../shared/CharacterTypes';
// Tabs for the left panel
import { EntityPalette } from './components/EntityPalette';
import { TeleporterConfig } from './components/TeleporterConfig';
import { EditorLoadOverlay } from './components/EditorLoadOverlay';
import { EditorNavigationControls } from './components/EditorNavigationControls';
import { storage } from '../../services/StorageService';
// Remaining standalone windows
import { BoundaryControlWindow } from '../Windows/BoundaryControlWindow';
import { EditorOptionsWindow } from './components/EditorOptionsWindow';
import { ShortcutsHelp } from './components/ShortcutsHelp';
import { ContextMenu } from './components/ContextMenu';
import { useKeyboardShortcut } from '../hooks/useKeyboardShortcut';

interface EditorOverlayProps {
  onExit: () => void;
}

/**
 * Editor Overlay using Layout System
 *
 * Key features:
 * - No pointer-events: none hacks
 * - Proper CSS Grid layout
 * - Resizable panels (drag the edge)
 * - Collapsible panels (click the ‹ › arrows)
 * - Persistent panel sizes to localStorage
 * - Keyboard shortcuts: Ctrl+B (left), Ctrl+L (right)
 */
export const EditorOverlay: React.FC<EditorOverlayProps> = ({ onExit }) => {
  const [editorState, setEditorState] = useState<EditorState | null>(null);
  const [isBoundaryControlOpen, setIsBoundaryControlOpen] = useState(false);
  const [isLoadOverlayOpen, setIsLoadOverlayOpen] = useState(false);
  const [isOptionsOpen, setIsOptionsOpen] = useState(false);
  const [isShortcutsOpen, setIsShortcutsOpen] = useState(false);

  // Layout context for input blocking
  const { isInteractingWithUI } = useLayout();

  // Sync state
  const [syncStatus, setSyncStatus] = useState<'synced' | 'local' | 'saving' | 'error' | 'idle'>('idle');
  const [isCloudAvailable, _setIsCloudAvailable] = useState(!!storage.get('helix_jwt'));

  // History state
  const [canUndo, setCanUndo] = useState(false);
  const [canRedo, setCanRedo] = useState(false);
  const [mapName, setMapName] = useState('Unknown Map');
  const [zoom, setZoom] = useState(1);

  // Left Panel Tab State
  const [activeLeftTab, setActiveLeftTab] = useState<'tiles' | 'entities' | 'teleporters' | 'fxLayers'>('tiles');

  // Test-play mode state
  const [isTestPlay, setIsTestPlay] = useState(false);

  // Selection state for PropertiesPanel
  const [selectedTile, setSelectedTile] = useState<{ type: TileType; x: number; y: number; layer: string } | undefined>(undefined);
  const [selectedEntity, setSelectedEntity] = useState<{ id: string; type: string; x: number; y: number; properties: Record<string, any> } | undefined>(undefined);

  const editorSystemRef = useRef<EditorSystem | null>(null);

  // Sync UI interaction state to engine
  useEffect(() => {
    editorSystemRef.current?.setIsUIInteracting(isInteractingWithUI);
  }, [isInteractingWithUI]);

  useEffect(() => {
    const registry = ServiceRegistry.getInstance();
    let system = registry.get('EditorSystem');

    // EditorSystem may not be registered yet at overlay mount time — the
    // scene's SceneSetupSystem instantiates + registers it during boot,
    // which can land after React mounts this component (especially when
    // `editorOn=1` opens the overlay before scene bootstrap completes).
    // Poll briefly; bail out once attached or after 5s.
    if (!system) {
      let attempts = 0;
      const retry = setInterval(() => {
        attempts++;
        system = registry.get('EditorSystem');
        if (system) {
          clearInterval(retry);
          attachToSystem(system);
        } else if (attempts >= 25) {
          // 25 × 200ms = 5s
          clearInterval(retry);
          gameLogger.error('editor', '❌ EditorSystem not found in registry after 5s — overlay will stay in Loading state');
        }
      }, 200);
      return () => clearInterval(retry);
    }

    return attachToSystem(system);

    /** Bind the overlay to the system once available. Returns cleanup fn. */
    function attachToSystem(sys: EditorSystem): () => void {
      editorSystemRef.current = sys;
      setEditorState(sys.getState());
      setMapName(sys.getMapName());

    const updateHandler = (newState: EditorState) => {
      setEditorState({ ...newState });
      setMapName(sys.getMapName());
    };

    // Listen for selection changes
    const selectionHandler = (data: any) => {
      if (data.type === 'tile' && data.tx !== undefined) {
        setSelectedTile({
          // Use the ACTUAL tile under the cursor (SelectTool reports it), not
          // the current brush — the panel must reflect what's really there.
          type: data.tileType ?? 0,
          x: data.tx,
          y: data.ty,
          layer: data.layer
        });
        setSelectedEntity(undefined);
      } else if (data.type === 'entity' && data.entity) {
        const entity: LevelEntity = data.entity;
        setSelectedEntity({
          id: entity.id,
          type: entity.type,
          x: entity.x,
          y: entity.y,
          properties: entity.properties || {}
        });
        setSelectedTile(undefined);
      } else if (data.type === 'none') {
        setSelectedTile(undefined);
        setSelectedEntity(undefined);
      }
    };

    const syncHandler = (_data: any) => {
      setSyncStatus('synced');
      setTimeout(() => setSyncStatus('idle'), 3000);
    };

    const localSaveHandler = (_data: any) => {
      setSyncStatus('local');
      setTimeout(() => setSyncStatus('idle'), 3000);
    };

    const saveFailedHandler = () => {
      setSyncStatus('error');
      setTimeout(() => setSyncStatus('idle'), 5000);
    };

    const historyHandler = (data: { canUndo: boolean; canRedo: boolean }) => {
      setCanUndo(data.canUndo);
      setCanRedo(data.canRedo);
    };

    const zoomHandler = (data: { zoom: number }) => {
      setZoom(data.zoom);
    };

    // Capture the disposers returned by on() — sys.off() was a no-op, so
    // discarding these leaked every editor listener on each overlay
    // mount/unmount cycle. The disposer (from SystemBase.subscribe) is the
    // real unsubscribe.
    const disposers = [
      sys.on('stateChanged', updateHandler),
      sys.on('selectionChanged', selectionHandler),
      sys.on('levelSynced', syncHandler),
      sys.on('levelSavedLocally', localSaveHandler),
      sys.on('levelSaveFailed', saveFailedHandler),
      sys.on('historyChanged', historyHandler),
      sys.on('zoomChanged', zoomHandler),
      sys.on('editor:testPlayEntered', () => setIsTestPlay(true)),
      sys.on('editor:testPlayExited', () => setIsTestPlay(false)),
    ];

    // Initial history state
    setCanUndo(sys.canUndo());
    setCanRedo(sys.canRedo());
    setMapName(sys.getMapName());
    setZoom(sys.getZoom());

    return () => {
      disposers.forEach(dispose => dispose());
    };
    } // end attachToSystem
  }, [editorState?.currentTile]);

  // Handlers
  const handleUndo = useCallback(() => {
    editorSystemRef.current?.undo();
  }, []);

  const handleRedo = useCallback(() => {
    editorSystemRef.current?.redo();
  }, []);

  // Keyboard Shortcuts
  useKeyboardShortcut('z', handleUndo, { ctrl: true }, [handleUndo]);
  useKeyboardShortcut('z', handleRedo, { ctrl: true, shift: true }, [handleRedo]);
  useKeyboardShortcut('y', handleRedo, { ctrl: true }, [handleRedo]);

  const handleToolChange = useCallback((tool: EditorTool) => {
    editorSystemRef.current?.setTool(tool);
  }, []);

  const handleTileSelect = useCallback((tileType: TileType) => {
    editorSystemRef.current?.setTile(tileType);
    editorSystemRef.current?.setTool(EditorTool.BRUSH);
  }, []);

  const handleSurfaceToggle = useCallback((enabled: boolean) => {
    editorSystemRef.current?.toggleSurface(enabled);
  }, []);

  const handleSurfaceTypeChange = useCallback((surfaceType: SurfaceType) => {
    editorSystemRef.current?.setSurfaceType(surfaceType);
  }, []);

  const handleBrushSizeChange = useCallback((size: number) => {
    editorSystemRef.current?.setBrushSize(size);
  }, []);

  const handleToggleGrid = useCallback(() => {
    editorSystemRef.current?.toggleGrid();
  }, []);

  // Tool-select hotkeys — each maps to a real, implemented tool.
  useKeyboardShortcut('b', () => handleToolChange(EditorTool.BRUSH), {}, [handleToolChange]);
  useKeyboardShortcut('e', () => handleToolChange(EditorTool.ERASER), {}, [handleToolChange]);
  useKeyboardShortcut('s', () => handleToolChange(EditorTool.SELECT), {}, [handleToolChange]);
  useKeyboardShortcut('f', () => handleToolChange(EditorTool.FILL), {}, [handleToolChange]);
  useKeyboardShortcut('r', () => handleToolChange(EditorTool.RECTANGLE), {}, [handleToolChange]);
  useKeyboardShortcut('l', () => handleToolChange(EditorTool.LINE), {}, [handleToolChange]);
  useKeyboardShortcut('n', () => handleToolChange(EditorTool.ENTITY_PLACER), {}, [handleToolChange]);
  useKeyboardShortcut('i', () => handleToolChange(EditorTool.EYEDROPPER), {}, [handleToolChange]);

  // Grid visibility toggle.
  useKeyboardShortcut('g', handleToggleGrid, {}, [handleToggleGrid]);

  // In-place test-play: T to enter, Escape to exit.
  // Unlike the full Test button (which reloads the page), this toggles
  // play mode in-place — no page reload, instant switch.
  const handleEnterTestPlay = useCallback(() => {
    editorSystemRef.current?.enterTestPlay();
  }, []);

  const handleExitTestPlay = useCallback(() => {
    editorSystemRef.current?.exitTestPlay();
  }, []);

  useKeyboardShortcut('t', handleEnterTestPlay, {}, [handleEnterTestPlay]);
  useKeyboardShortcut('Escape', handleExitTestPlay, {}, [handleExitTestPlay]);

  // Brush-size hotkeys (1-5).
  useKeyboardShortcut('1', () => handleBrushSizeChange(1), {}, [handleBrushSizeChange]);
  useKeyboardShortcut('2', () => handleBrushSizeChange(2), {}, [handleBrushSizeChange]);
  useKeyboardShortcut('3', () => handleBrushSizeChange(3), {}, [handleBrushSizeChange]);
  useKeyboardShortcut('4', () => handleBrushSizeChange(4), {}, [handleBrushSizeChange]);
  useKeyboardShortcut('5', () => handleBrushSizeChange(5), {}, [handleBrushSizeChange]);

  const handleLayerSelect = useCallback((layer: LayerType) => {
    editorSystemRef.current?.setLayer(layer);
  }, []);

  const handleSave = useCallback(async () => {
    setSyncStatus('saving');
    await editorSystemRef.current?.saveLevel();
  }, []);

  const handleTest = useCallback(() => {
    // Test-play the map you just built. saveToTestMap() persists the current
    // edit to the `dev-map` workspace (localStorage); we then RELOAD onto a
    // plain play surface that loads `dev-map` (BootService honors ?map=dev-map
    // → LevelStorageService). Previously this only called onExit(), which left
    // you on the editor's boot map (empty-canvas) — "Test took you to the old
    // map" instead of the one you edited.
    const saved = editorSystemRef.current?.saveToTestMap();
    if (!saved) {
      setSyncStatus('error');
      gameLogger.warn('editor', '🎮 Test aborted — could not save the dev-map workspace');
      return; // never navigate on a failed save (no false success)
    }
    gameLogger.info('editor', '🎮 Testing level — reloading onto the edited map (dev-map)');
    if (typeof window !== 'undefined') {
      const sp = new URLSearchParams();
      sp.set('map', 'dev-map');        // play the just-saved edit, not empty-canvas
      sp.set('skipSplash', '1');
      sp.set('skipLogin', '1');
      sp.set('pickCharacter', '0');    // auto-spawn — do NOT force the class picker
      sp.set('keepCharacter', '1');    // reuse the last-picked character (bridge)
      sp.set('fromEditor', '1');       // test surface shows the "← Back to Editor" pill
      window.location.href = `${window.location.origin}/?${sp.toString()}`;
    } else {
      onExit();
    }
  }, [onExit]);

  const handleNewMap = useCallback(() => {
    if (window.confirm('Are you sure you want to create a new map? Unsaved changes will be lost.')) {
      editorSystemRef.current?.resetToStarterMap();
    }
  }, []);

  const handleLoadMap = useCallback(() => {
    setIsLoadOverlayOpen(true);
  }, []);

  const handleLevelFileLoad = useCallback((mapId: string) => {
    editorSystemRef.current?.loadLevel(mapId);
    setIsLoadOverlayOpen(false);
  }, []);

  const handlePropertyChange = useCallback((property: string, value: any) => {
    // Tile selection: the only meaningful action is "Clear Tile" (tiles are
    // grid-fixed, so X/Y are read-only). Route it to the real erase path so
    // the button isn't a dead facade.
    if (selectedTile) {
      if (property === 'type' && value === TileType.EMPTY) {
        editorSystemRef.current?.clearSelectedTile();
        setSelectedTile(undefined);
      }
      return;
    }

    if (property === 'remove' && selectedEntity) {
      editorSystemRef.current?.deleteEntity(selectedEntity.id);
      setSelectedEntity(undefined);
    } else if (selectedEntity) {
      editorSystemRef.current?.updateSelectedEntityProperty(property, value);
      // Update local state to reflect change
      if (property === 'x' || property === 'y') {
        setSelectedEntity({ ...selectedEntity, [property]: value });
      } else {
        setSelectedEntity({
          ...selectedEntity,
          properties: { ...selectedEntity.properties, [property]: value }
        });
      }
    }
  }, [selectedEntity, selectedTile]);

  if (!editorState) {
    return <div style={{ color: 'white', padding: 20 }}>Loading Editor System...</div>;
  }

  // Test-play mode: hide all editor UI, show only the "Press ESC" banner.
  // The game canvas is still visible underneath — the player can move
  // and interact with the level as in normal gameplay.
  if (isTestPlay) {
    return (
      <div style={{
        position: 'fixed',
        top: 16,
        left: '50%',
        transform: 'translateX(-50%)',
        background: 'rgba(0, 0, 0, 0.75)',
        color: '#7CFC00',
        padding: '8px 20px',
        borderRadius: '8px',
        fontFamily: 'monospace',
        fontSize: '14px',
        fontWeight: 'bold',
        zIndex: 10000,
        border: '1px solid #7CFC00',
        pointerEvents: 'none',
        userSelect: 'none',
      }}>
        🎮 Test Play — Press ESC to return to editor
      </div>
    );
  }

  return (
    <>
      <AppContainer
        storageKey="helix-editor-layout"
        header={
          <Toolbar
            currentTool={editorState.currentTool}
            onToolChange={handleToolChange}
            gridVisible={editorState.gridVisible}
            onGridToggle={() => editorSystemRef.current?.toggleGrid()}
            snapToGrid={editorState.snapToGrid}
            onSnapToggle={() => editorSystemRef.current?.toggleSnap()}
            showCollision={editorState.showCollision}
            onCollisionToggle={() => editorSystemRef.current?.toggleCollision()}
            onNewMap={handleNewMap}
            onLoadMap={handleLoadMap}
            onSave={handleSave}
            onTest={handleTest}
            onExit={onExit}
            onOpenBoundaryControl={() => setIsBoundaryControlOpen(true)}
            onOpenOptions={() => setIsOptionsOpen(true)}
            onOpenShortcuts={() => setIsShortcutsOpen(true)}
            syncStatus={syncStatus}
            isCloudAvailable={isCloudAvailable}
            canUndo={canUndo}
            canRedo={canRedo}
            onUndo={handleUndo}
            onRedo={handleRedo}
            mapName={mapName}
          />
        }
        leftPanel={
          <Panel
            title="Editor Tools"
            icon="🛠️"
            position="left"
            defaultWidth={300}
          >
            <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
              <div style={{ display: 'flex', borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
                <button
                  onClick={() => setActiveLeftTab('tiles')}
                  style={{ flex: 1, padding: '8px', background: activeLeftTab === 'tiles' ? 'rgba(255,255,255,0.1)' : 'transparent', border: 'none', color: 'white', cursor: 'pointer', fontSize: '12px' }}
                >
                  🎨 Tiles
                </button>
                <button
                  onClick={() => setActiveLeftTab('entities')}
                  style={{ flex: 1, padding: '8px', background: activeLeftTab === 'entities' ? 'rgba(255,255,255,0.1)' : 'transparent', border: 'none', color: 'white', cursor: 'pointer', fontSize: '12px' }}
                >
                  🎭 Entities
                </button>
                <button
                  onClick={() => setActiveLeftTab('teleporters')}
                  style={{ flex: 1, padding: '8px', background: activeLeftTab === 'teleporters' ? 'rgba(255,255,255,0.1)' : 'transparent', border: 'none', color: 'white', cursor: 'pointer', fontSize: '12px' }}
                >
                  ⬌ Portals
                </button>
                <button
                  onClick={() => setActiveLeftTab('fxLayers')}
                  style={{ flex: 1, padding: '8px', background: activeLeftTab === 'fxLayers' ? 'rgba(255,255,255,0.1)' : 'transparent', border: 'none', color: 'white', cursor: 'pointer', fontSize: '12px' }}
                >
                  🌫️ FX
                </button>
              </div>
              <div style={{ flex: 1, overflowY: 'auto', overflowX: 'hidden' }}>
                <div style={{ display: activeLeftTab === 'tiles' ? 'block' : 'none', height: '100%' }}>
                  <TilePalette
                    currentTile={editorState.currentTile}
                    onTileSelect={handleTileSelect}
                    brushSize={editorState.brushSize}
                    onBrushSizeChange={handleBrushSizeChange}
                    hasSurface={editorState.hasSurface}
                    surfaceType={editorState.surfaceType}
                    onSurfaceToggle={handleSurfaceToggle}
                    onSurfaceTypeChange={handleSurfaceTypeChange}
                  />
                </div>
                <div style={{ display: activeLeftTab === 'entities' ? 'block' : 'none', height: '100%' }}>
                  <EntityPalette
                    currentEntityId={selectedEntity?.id || null}
                    currentEntityType={selectedEntity?.type as any || null}
                    onEntitySelect={(id, type) => {
                      // Map entity type to editor system type just like EntityPaletteWindow did
                      const systemType = type === 'mob' ? 'mob' : type === 'npc' ? 'npc' : 'object';
                      editorSystemRef.current?.setEntity(id, systemType as any);
                      editorSystemRef.current?.setTool(EditorTool.ENTITY_PLACER);
                    }}
                  />
                </div>
                <div style={{ display: activeLeftTab === 'teleporters' ? 'block' : 'none', height: '100%' }}>
                  <TeleporterConfig editorSystem={editorSystemRef.current} />
                </div>
                <div style={{ display: activeLeftTab === 'fxLayers' ? 'block' : 'none', height: '100%' }}>
                  <EffectLayerPanel editorSystem={editorSystemRef.current} active={activeLeftTab === 'fxLayers'} />
                </div>
              </div>
            </div>
          </Panel>
        }
        rightPanel={
          <Panel position="right" collapsible icon="🎛️" title="Controls">
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              height: '100%',
              width: '100%'
            }}>
            <div style={{ flex: selectedTile || selectedEntity ? '0 0 40%' : 1, display: 'flex', flexDirection: 'column', minHeight: 200 }}>
              <div style={{ padding: '8px 15px', backgroundColor: '#2c3e50', borderBottom: '1px solid #1a252f', fontWeight: 'bold', fontSize: '14px', color: 'white' }}>
                📋 Layers
              </div>
              <div style={{ flex: 1, overflowY: 'auto', padding: '15px' }}>
                <LayerPanel
                  layers={editorState.layers}
                  activeLayer={editorState.currentLayer}
                  onLayerSelect={handleLayerSelect}
                  onLayerVisibilityToggle={(id) => editorSystemRef.current?.toggleLayerVisibility(id)}
                  onLayerLockToggle={(id) => editorSystemRef.current?.toggleLayerLock(id)}
                  onOpacityChange={(id, val) => editorSystemRef.current?.setLayerOpacity(id, val)}
                  onAddLayer={() => editorSystemRef.current?.addLayer()}
                  onRemoveLayer={(id) => editorSystemRef.current?.removeLayer(id)}
                  onMoveLayerUp={(id) => editorSystemRef.current?.moveLayerUp(id)}
                  onMoveLayerDown={(id) => editorSystemRef.current?.moveLayerDown(id)}
                />
              </div>
            </div>

            {/* Properties Panel - shows when something is selected */}
            {(selectedTile || selectedEntity) && (
              <div style={{ flex: '1 1 60%', display: 'flex', flexDirection: 'column', minHeight: 200, borderTop: '2px solid #1a252f' }}>
                <div style={{ padding: '8px 15px', backgroundColor: '#2c3e50', borderBottom: '1px solid #1a252f', fontWeight: 'bold', fontSize: '14px', color: 'white' }}>
                  ⚙️ Properties
                </div>
                <div style={{ flex: 1, overflowY: 'auto', padding: '15px' }}>
                  <PropertiesPanel
                    selectedTile={selectedTile}
                    selectedEntity={selectedEntity}
                    onPropertyChange={handlePropertyChange}
                  />
                </div>
              </div>
            )}
            </div>
          </Panel>
        }
      >
        {/*
          Main content area - the game canvas goes here
          The canvas is rendered by the game host BELOW this React component,
          but the layout system handles the sizing properly.
        */}
        <div style={{ width: '100%', height: '100%', position: 'relative' }}>
          <EditorNavigationControls zoom={zoom} />
        </div>
      </AppContainer>

      {/* Floating windows (outside the layout system) */}
      <BoundaryControlWindow
        isOpen={isBoundaryControlOpen}
        onClose={() => setIsBoundaryControlOpen(false)}
        editorSystem={editorSystemRef.current}
      />

      <EditorOptionsWindow
        isOpen={isOptionsOpen}
        onClose={() => setIsOptionsOpen(false)}
        state={editorState}
        editorSystem={editorSystemRef.current}
      />

      <EditorLoadOverlay
        isVisible={isLoadOverlayOpen}
        onLoad={handleLevelFileLoad}
        onCancel={() => setIsLoadOverlayOpen(false)}
      />

      <ShortcutsHelp
        isOpen={isShortcutsOpen}
        onClose={() => setIsShortcutsOpen(false)}
      />

      {/* Right-click context menu on canvas */}
      <ContextMenu />
    </>
  );
};
